/**
 * One paginator for plain MUI <Table> lists: 25 rows a page, with 50 / 100.
 *
 *   const { pageRows, paginationProps } = usePagedRows(rows)
 *   ...map pageRows into <TableBody>...
 *   <ListPagination {...paginationProps} />
 *
 * Client-side today: the hook slices rows already in memory. A list that pages
 * on the server can still render <ListPagination> and pass its own
 * count / page / rowsPerPage / handlers.
 */
import { TablePagination } from '@material-ui/core'
import React, { useEffect, useMemo, useState } from 'react'

export const PAGE_SIZE_OPTIONS = [25, 50, 100]
export const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0]

export const usePagedRows = (rows, { pageSize = DEFAULT_PAGE_SIZE } = {}) => {
  const list = Array.isArray(rows) ? rows : []
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(pageSize)

  // A filter change or a delete can leave us past the last page — step back
  // to the last page that still has rows instead of showing an empty table.
  const lastPage = Math.max(0, Math.ceil(list.length / rowsPerPage) - 1)
  useEffect(() => {
    if (page > lastPage) setPage(lastPage)
  }, [page, lastPage])

  const current = Math.min(page, lastPage)
  const pageRows = useMemo(
    () => list.slice(current * rowsPerPage, (current + 1) * rowsPerPage),
    [list, current, rowsPerPage]
  )

  return {
    pageRows,
    resetPage: () => setPage(0),
    paginationProps: {
      count: list.length,
      page: current,
      rowsPerPage,
      onPageChange: (_, next) => setPage(next),
      onRowsPerPageChange: (e) => {
        // Read synchronously — React 16 pools synthetic events.
        const size = parseInt(e.target.value, 10)
        setRowsPerPage(size)
        setPage(0)
      }
    }
  }
}

const ListPagination = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = PAGE_SIZE_OPTIONS
}) => {
  // Nothing to page through — don't show a dead control.
  if (count <= Math.min(...rowsPerPageOptions)) return null
  return (
    <TablePagination
      component="div"
      count={count}
      page={page}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={rowsPerPageOptions}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
    />
  )
}

export default ListPagination
